"use client";

import Image from "next/image";
import { PauseIcon, PlayIcon } from "@phosphor-icons/react/ssr";
import ElementorContainer from "~/componentsDS/ElementorLoyout/ElementorContainer";
import Heading from "~/componentsDS/Typography/Heading";
import Overline from "~/componentsDS/Typography/Overline";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import { cn } from "~/lib/utils";
import Badge from "~/componentsDS/Badge";
import Reveal from "~/componentsDS/Reveal";
import useVslTracking from "~/hooks/useVslTracking";
import VideoPlayer from "~/componentsDS/VideoPlayer";

const FounderExpertSection = () => {
    const { videoRef, videoStarted, onTogglePlay, onVideoPlay, onVideoPause, onVideoEnded } = useVslTracking();

    return (
        <section className="relative py-4 md:py-0 bg-white lg:bg-gray100 rounded-2xl md:rounded-3xl overflow-hidden">
            <ElementorContainer className="relative" innerClassName="pt-9">
                <div className="flex flex-col lg:flex-row lg:gap-10 items-center text-primary500 lg:h-[500px] w-full">
                    <Reveal className="lg:order-2 w-full lg:w-[36%] mb-6 lg:mb-0 ">
                        <Badge variant="primary" className="lg:hidden font-medium mb-4">Meet Dr. Sarah</Badge>
                        <div className="max-w-full max-w-[340px] md:max-w-[440px]">
                            <Heading as="h2" size="sm" sizes={{md: "md"}} className="tracking-[0.01em] mb-4 md:mb-6">
                                Backed by science. <br className="sm:hidden" />
                                Formulated by women&apos;s health experts.
                            </Heading>

                            <Paragraph size="lg" className="max-w-[340px] md:max-w-[390px]">
                                Inner Balance was founded by Dr. Sarah Daccarett, MD, a board-certified specialist in women&apos;s hormonal health and longevity medicine.
                                <br/><br/>
                                Her research has been published in leading medical journals, and she is licensed to practice medicine in 50 states.
                            </Paragraph>
                        </div>
                    </Reveal>

                    <div className="lg:order-1 relative mt-auto lg:min-h-[500px] lg:w-[40%]">
                        <Reveal className="relative z-0 h-auto w-full">
                            <Image
                                src="https://res.cloudinary.com/dirscuuhp/images/f_auto,q_auto/v1775044212/doc-sarah-black-mob/doc-sarah-black-mob.webp"
                                alt="Dr. Sarah, Inner Balance women's health clinician"
                                width={400}
                                height={474}
                                className="block h-[474px] w-full object-cover object-bottom md:hidden"
                            />

                            <Image
                                src="https://res.cloudinary.com/dirscuuhp/images/f_auto,q_auto/v1775035493/doc-sara-black/doc-sara-black.webp"
                                alt="Dr. Sarah, Inner Balance women's health clinician"
                                width={400}
                                height={500}
                                className="hidden h-[500px] lg:mx-auto object-contain object-bottom md:block"
                            />
                        </Reveal>

                        <div className="absolute left-1/2 -translate-x-1/2 bottom-8 w-[290px] z-10 flex flex-col items-center gap-1 rounded-lg border border-pureWhite/10 bg-trueBlack/25 p-4 text-center backdrop-blur-[25px] md:left-1/2 md:bottom-6 md:right-auto md:w-[98%] md:max-w-[385px] md:-translate-x-1/2">
                            <h3 className="font-featureDisplay text-[20px] leading-[1.2] tracking-[0.01em] text-pureWhite">
                                Dr. Sarah Daccarett, MD
                            </h3>

                            <Badge variant="secondary" className="font-medium">
                                Founder & CEO, Inner Balance
                            </Badge>
                        </div>
                    </div>

                    <div className="lg:order-3 hidden lg:flex lg:flex-col gap-3 p-4 bg-pureWhite rounded-xl lg:w-[21%] lg:mb-9 shadow-[0_2px_4px_-2px_rgba(0,0,0,0.05),_0_4px_6px_-1px_rgba(0,0,0,0.05)]">
                        <div className="group vsl-player-sm-2 rounded-xl overflow-hidden relative w-full cursor-pointer ">
                            <VideoPlayer
                                ref={videoRef}
                                mp4="https://res.cloudinary.com/dirscuuhp/video/upload/v1769694160/new_design/Oestra-HRT-Education-Vaginal_Dryness-VSL-9X16-AV2-HV1-BV1-CV1-LP1-2025_11_5_nl2onu.mp4"
                                hls="https://res.cloudinary.com/dirscuuhp/video/upload/v1769694160/new_design/Oestra-HRT-Education-Vaginal_Dryness-VSL-9X16-AV2-HV1-BV1-CV1-LP1-2025_11_5_nl2onu.m3u8"
                                width="310"
                                height="414"
                                poster="https://res.cloudinary.com/dirscuuhp/image/upload/v1768993493/new_design/2d4ed36e63556a1de3ed8b78a8492fba7c31ce25_cq0mxi.webp"
                                title="Dr. Sarah Daccarett"
                                controlsList="nofullscreen"
                                playsInline
                                onPlay={onVideoPlay}
                                onPause={onVideoPause}
                                onEnded={onVideoEnded}
                                onClick={onTogglePlay}
                                className={cn(
                                    "nofullscreen w-full aspect-[310/414] object-cover object-top bg-trueBlack",
                                    "transition duration-300 ease-in-out",
                                    !videoStarted && "group-hover:scale-105"
                                )}
                            />

                            <button
                                type="button"
                                className={cn(
                                    "w-14 h-14",
                                    "flex items-center justify-center",
                                    "border-2 border-pureWhite/25 group-hover:border-primary200 bg-pureWhite/5 group-hover:bg-primary200 backdrop-blur-md rounded-full",
                                    "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
                                    "transition duration-300 ease-in-out",
                                    videoStarted ? "opacity-0 group-hover:animate-[fade-out_2s_forwards]" : "",
                                )}
                                onClick={onTogglePlay}
                                aria-label={videoStarted ? "Pause video" : "Play video"}
                                aria-pressed={videoStarted}
                            >
                                {videoStarted ? (
                                    <PauseIcon weight="fill" size={28} fill="white" />
                                ) : (
                                    <PlayIcon weight="fill" size={28} fill="white" />
                                )}
                            </button>
                        </div>
                        <Overline size="lg" className="tracking-[1.28px] max-md:text-trueBlack text-center">Hear from Dr Sarah</Overline>
                    </div>
                </div>
            </ElementorContainer>
        </section>
    )
};

export default FounderExpertSection;
